/**
 * Является точной копией @consta/uikit/Popover версии 5.12.0,
 * с той лишь разницей, что можно выбрать контейнер, к которому крепится поповер
 * Скопированы хелперы, дополнительные компоненты, стили
 * Заменены внутренние хуки аналогичными
 */
import { directions, usePopoverReposition } from '@consta/uikit/Popover'
import { PortalWithTheme } from '@consta/uikit/PortalWithTheme'
import { useTheme } from '@consta/uikit/Theme'
import { useForkRef } from '@consta/uikit/useForkRef'
import { useAutoResize, useLatest } from '@library/hooks'
import cn from 'classnames'
import type { Nullable } from 'common/types'
import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { ContextConsumer } from './ContextConsumer'
import {
  getComputedPositionAndDirection,
  getOffset,
  getPointPosition,
  getRenderPosition,
} from './helpers'
import css from './PlaceablePopover.css'
import type { Direction, PopoverProps } from './types'

export const PlaceablePopover = forwardRef<HTMLDivElement, PopoverProps>(
  function PlaceablePopover(props, componentRef) {
    const {
      children,
      direction: passedDirection = 'upCenter',
      offset: propOffset = 0,
      arrowOffset,
      possibleDirections = directions,
      isInteractive = true,
      onClickOutside,
      spareDirection = 'downStartLeft',
      style,
      className,
      position: passedPosition,
      anchorRef,
      equalAnchorWidth,
      onSetDirection,
      viewportRef,
      container = document.body,
      ...otherProps
    } = props

    const viewportElement = viewportRef?.current || document.documentElement

    const [containerElement, setContainerElement] = useState<Nullable<HTMLDivElement>>(null)
    const containerElementLatest = useLatest(containerElement)
    const { theme } = useTheme()

    const [anchorClientRect, setAnchorClientRect] = useState<DOMRect | undefined>()
    const { width, height } = useAutoResize(containerElement)
    const anchorSize = useAutoResize(anchorRef?.current ?? null)
    const previousDirectionRef = useRef<Direction | null>(null)
    const [bannedDirections, setBannedDirections] = useState<readonly Direction[]>([])

    const resetBannedDirections = () => {
      setBannedDirections(state => (state.length ? [] : state))
      previousDirectionRef.current = null
    }

    const updateAnchorClientRect = () => {
      setAnchorClientRect(anchorRef?.current?.getBoundingClientRect())
    }

    const isHasRef = Boolean(containerElement)
    const offset = useMemo(
      () => getOffset(containerElementLatest, propOffset),
      [propOffset, isHasRef],
    )

    const { position, direction } = getComputedPositionAndDirection({
      contentSize: { width, height },
      viewportSize: {
        width: viewportElement.clientWidth,
        height: viewportElement.clientHeight,
      },
      arrowOffset,
      offset,
      direction: passedDirection,
      possibleDirections,
      bannedDirections,
      position: getPointPosition(
        viewportElement,
        anchorClientRect ? { x: anchorClientRect.left, y: anchorClientRect.top } : passedPosition,
        !!viewportRef?.current,
      ),
      anchorSize,
      spareDirection,
    })

    useEffect(() => onSetDirection?.(direction), [direction])

    useEffect(updateAnchorClientRect, [anchorSize])

    usePopoverReposition({
      isActive: true,
      scrollAnchorRef: anchorRef || { current: null },
      onRequestReposition: () => {
        resetBannedDirections()
        updateAnchorClientRect()
      },
    })

    /**
     * Может возникнуть ситуация, когда перерасчет поповера всегда будет выдавать 2 направления
     * и бесконечно зацикливать себя. Для избежания таких кейсов мы запоминаем стороны,
     * которые не подошли, чтобы не возвращаться к ним и предотвратить бесконечный ререндер.
     * См. PopoverBannedPositionsStory
     */

    useLayoutEffect(() => {
      if (previousDirectionRef.current !== direction) {
        if (
          previousDirectionRef.current &&
          !bannedDirections.includes(previousDirectionRef.current) &&
          !bannedDirections.includes(direction) &&
          direction !== spareDirection
        ) {
          setBannedDirections(state =>
            previousDirectionRef.current ? [...state, previousDirectionRef.current] : state,
          )
        }
        previousDirectionRef.current = direction
      }
    }, [direction])

    // Сбрасываем при любом изменении пропсов, чтобы заново начать перебор направлений
    // Главное не сбрасывать при изменении размеров поповера, т.к. именно оно может вызвать бесконечный перебор

    useLayoutEffect(resetBannedDirections, [props])

    const renderPosition = getRenderPosition(
      viewportElement,
      position,
      !!viewportRef?.current,
      width,
      height,
    )

    const notVisible = !renderPosition || !height || !width

    return (
      <PortalWithTheme
        {...otherProps}
        ref={useForkRef([setContainerElement, componentRef])}
        preset={theme}
        className={cn(css.Popover, { [css.notVisible]: notVisible }, className)}
        container={container}
        style={{
          ...style,
          ...(notVisible
            ? {}
            : {
                ['--popover-left']: `${renderPosition.x}px`,
                ['--popover-top']: `${renderPosition.y}px`,
                ['--popover-width']: equalAnchorWidth ? `${anchorSize.width}px` : undefined,
                ['--popover-pointer-events']: isInteractive ? undefined : 'none',
                ['--popover-visibility']: position ? undefined : 'hidden',
              }),
        }}>
        <ContextConsumer
          ignoreClicksInsideRefs={[containerElementLatest, anchorRef || { current: null }]}
          onClickOutside={onClickOutside}>
          {children}
        </ContextConsumer>
      </PortalWithTheme>
    )
  },
)
